import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, Building2, Pencil, Trash2, Loader2, CheckCircle, UserCheck } from "lucide-react";
import { toast } from "sonner";

function CustomerForm({ customer, onSaved, onClose }) {
  const isEdit = !!customer;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: customer?.name || "",
    business_id: customer?.business_id || "",
    contact_person: customer?.contact_person || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
    address: customer?.address || "",
    postal_code: customer?.postal_code || "",
    city: customer?.city || "",
    country: customer?.country || "Suomi",
    notes: customer?.notes || "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name?.trim()) {
      toast.error("Nimi puuttuu");
      return;
    }
    setLoading(true);
    try {
      if (isEdit) await api.put(`/customers/${customer.id}`, form);
      else await api.post("/customers", form);
      toast.success(isEdit ? "Asiakas päivitetty" : "Asiakas lisätty");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="customer-registry-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">Yrityksen / asiakkaan nimi *</label>
          <Input value={form.name} onChange={set("name")} placeholder="Nimi" className="mt-1" data-testid="customer-name" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Y-tunnus</label>
          <Input value={form.business_id} onChange={set("business_id")} placeholder="1234567-8" className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Yhteyshenkilö</label>
          <Input value={form.contact_person} onChange={set("contact_person")} placeholder="Nimi" className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Sähköposti</label>
          <Input value={form.email} onChange={set("email")} placeholder="email@yritys.fi" className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Puhelin</label>
          <Input value={form.phone} onChange={set("phone")} placeholder="+358 40..." className="mt-1" />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">Katuosoite</label>
          <Input value={form.address} onChange={set("address")} placeholder="Katuosoite" className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Postinumero</label>
          <Input value={form.postal_code} onChange={set("postal_code")} placeholder="00100" className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Kaupunki</label>
          <Input value={form.city} onChange={set("city")} placeholder="Helsinki" className="mt-1" />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">Huomiot</label>
          <Input value={form.notes} onChange={set("notes")} placeholder="Lisätiedot..." className="mt-1" />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Peruuta
        </Button>
        <Button onClick={save} disabled={loading} className="flex-1 gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900" data-testid="customer-save">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          {isEdit ? "Tallenna" : "Lisää asiakas"}
        </Button>
      </div>
    </div>
  );
}

export default function CustomerRegistryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/customers");
      setItems(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Poistetaanko asiakas?")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Asiakas poistettu");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = items.filter(
    (c) =>
      (c.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.business_id || "").includes(search) ||
      (c.email || "").toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-4" data-testid="customer-registry-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hae asiakasta..."
            className="pl-9"
            data-testid="customer-registry-search"
          />
        </div>
        <Button
          size="sm"
          className="gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900"
          onClick={() => {
            setEditItem(null);
            setShowForm(true);
          }}
          data-testid="customer-registry-new"
        >
          <Plus className="w-4 h-4" /> Uusi asiakas
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Ladataan...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p>Ei asiakkaita vielä. Lisää ensimmäinen!</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {filtered.map((c) => (
            <Card key={c.id} className="hover:bg-muted/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-4 h-4 text-amber-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">{c.name}</p>
                      <div className="flex flex-wrap gap-2 mt-0.5">
                        {c.business_id && <span className="text-xs text-muted-foreground">Y-tunnus: {c.business_id}</span>}
                        {c.email && <span className="text-xs text-muted-foreground">{c.email}</span>}
                        {c.city && <span className="text-xs text-muted-foreground">{c.city}</span>}
                        {c.invoices_count > 0 && (
                          <Badge variant="outline" className="text-[10px] h-4">
                            {c.invoices_count} laskua
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="w-7 h-7"
                      onClick={() => {
                        setEditItem(c);
                        setShowForm(true);
                      }}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="w-7 h-7 text-destructive/60 hover:text-destructive"
                      onClick={() => remove(c.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editItem ? "Muokkaa asiakasta" : "Uusi asiakas"}</DialogTitle>
          </DialogHeader>
          <CustomerForm
            customer={editItem}
            onSaved={() => {
              setShowForm(false);
              load();
            }}
            onClose={() => setShowForm(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
